import localhost, { downloadFileFromResponse } from "apis/localhost";
import { showErrorModal } from "slices/app/actions/error";
import { removeErrorMessage } from "slices/app/errorSlice";
import { modalTypes } from "constants/types";
import { hideModal, showModal } from "slices/app/actions/modals";
import { userAdminImportUsersFromCsvConfirmationModal } from "components/Modal/confModalTypes";
import {
  addToJobQueue,
  clearJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import {
  setWorkspaceInfo,
  setUsersInfo,
} from "slices/userAdmin/userAdminSlice";

export const fetchWorkspaceInfo = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/userAdmin/getWorkspace")
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspaceInfo(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const userAdminRemoveUserFromWorkspace =
  (userName, workspaceId, workspaceName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .delete(`/docs/userAdmin/workspace/user`, {
        data: { userName },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(removeErrorMessage);
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(setWorkspaceInfo(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `${userName} successfully removed from ${workspaceName}`,
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const createUserAndAddToWorkspace =
  (userName, firstName, lastName, password) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post("/docs/userAdmin/workspace/user", {
        userName,
        firstName,
        lastName,
        password,
      })
      .then(async (response) => {
        const { data } = response;

        dispatch(setWorkspaceInfo(data));
        dispatch(hideModal(modalTypes.NEW_USER));

        if (
          data.workspaces[0].users.filter(
            (user) => user.username === userName
          )[0].licensed
        ) {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: `${userName} has been created and has been added to the ${data.workspaces[0].name} workspace as a “Licensed User“`,
            })
          );
        } else {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.warning,
              text: `${userName} has been created and added to the ${data.workspaces[0].name} workspace as a member. They cannot be Licensed as you have already reached the maximum number of licensed users. Please de-activate a different user and try again`,
            })
          );
        }
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.NEW_USER));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const importUsersWithRolesFromCsv = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/docs/userAdmin/importUsers", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspaceInfo(data.workspacesResponse));
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          userAdminImportUsersFromCsvConfirmationModal(
            data.usersReportString,
            data.message,
            dispatch
          )
        )
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const exportResultFromImportUsers = (results) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/userAdmin/exportUsersResult", {
      responseType: "blob",
      params: { usersString: results },
    })
    .then(async (response) => {
      const { data } = response;

      downloadFileFromResponse(data, "BulkUserResults.csv");

      dispatch(hideModal(modalTypes.CONFIRMATION));
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const editUser =
  (
    userName,
    firstName,
    lastName,
    password,
    licensed,
    userAdmin,
    readabilitySettingsAdmin,
    acronymSettingsAdmin,
    discoverySettingsAdmin,
    excelOutputsAdmin,
    dictionariesAdmin
  ) =>
  async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .put("/docs/userAdmin/workspace/user", {
        userName,
        firstName,
        lastName,
        password,
        licensed,
        userAdmin,
        readabilitySettingsAdmin,
        acronymSettingsAdmin,
        discoverySettingsAdmin,
        excelOutputsAdmin,
        dictionariesAdmin,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setWorkspaceInfo(data));
        dispatch(hideModal(modalTypes.EDIT_USER_AND_PERMISSIONS));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: "User Successfully Updated",
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.EDIT_USER_AND_PERMISSIONS));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

const getAddUsersToWorkspaceResultBanner = (
  users,
  currentLicensedUsers,
  response
) => {
  const username = users[0];
  const workspaceName = response.workspaces[0].name;
  const workspaceReservedLicenses = response.workspaces[0].reservedLicenses;
  const { licensed } = response.workspaces[0].users.find(
    (user) => user.username === username
  );

  if (licensed) {
    return {
      variant: alertVariants.success,
      text: `The selected user/s have been successfully added to the ${workspaceName} workspace as Licensed user/s.`,
    };
  }
  if (workspaceReservedLicenses - currentLicensedUsers - users.length < 0) {
    return {
      variant: alertVariants.warning,
      text: `The selected user/s have been successfully added to the ${workspaceName} workspace but have not been licensed. You have reached the maximum number of licensed users for this workspace. Please de-activate a different user and try again.`,
    };
  }
  return {
    variant: alertVariants.warning,
    text: `The selected user/s have been successfully added to the ${workspaceName} workspace but have not been licensed. You have reached the maximum number of licensed users. Please de-activate a different user and try again.`,
  };
};

export const addUsersToWorkspace =
  (users, currentLicensedUsers) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .put("/docs/userAdmin/workspace/users", { users })
      .then(async (response) => {
        const { data } = response;
        const bannerOptions = getAddUsersToWorkspaceResultBanner(
          users,
          currentLicensedUsers,
          data
        );
        dispatch(setWorkspaceInfo(data));
        dispatch(hideModal(modalTypes.ADD_USER_TO_WORKSPACE));
        dispatch(showGlobalAlert(bannerOptions));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const getUsers = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/userAdmin/users")
    .then(async (response) => {
      const { data } = response;
      dispatch(setUsersInfo(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};
